import type { ErrorCode } from '@volee/api-client'

// The API error catalogue, SOURCE language. `Record<ErrorCode, string>` is the guarantee: a
// code added on the backend without a text here does not compile. Register: this app's audience.
export const EN_ERRORS: Record<ErrorCode, string> = {
  'error.unauthenticated': 'Your session has expired. Sign in again to continue.',
  'error.forbidden': 'Your profile does not have permission for this operation.',
  'error.not_found': 'The requested resource does not exist.',
  'error.conflict': 'The operation conflicts with the current state of the data.',
  'error.unprocessable': 'The data sent cannot be processed.',
  'error.internal': 'Internal error. Try again in a moment.',
  'error.http': 'Invalid request.',
  'error.validation_failed': 'Some fields are not filled in correctly.',
  'error.unique_violation': 'This value has already been used.',
  'error.foreign_key_violation': 'The reference given does not exist.',
  'error.check_violation': 'A value does not satisfy the constraints.',
  'error.not_null_violation': 'A required field is missing.',
  'error.constraint_violation': 'The data does not satisfy a constraint.',
  'error.identity_provider_unreachable':
    'The authentication service is not responding. Try again in a moment.',
  'auth.credentials_missing': 'Credentials missing. Sign in to continue.',
  'auth.token_invalid': 'Invalid session. Sign in again.',
  'auth.token_not_id': 'Invalid session. Sign in again.',
  'auth.account_not_linked': 'This account is not linked to any Volée profile.',
  'auth.account_deactivated': 'This account is deactivated.',
  'auth.staff_required': 'The back office is reserved for Volée staff.',
  'auth.admin_required': 'Inviting staff is reserved for administrators.',
  'auth.not_your_club': 'This club is not yours.',
  'auth.not_your_match': "This match is not one of your teams'.",
  'auth.not_your_team': 'This team is not yours.',
  'auth.not_your_person': 'This anagrafica is not yours.',
  'auth.password_verification_required': 'The account’s password is needed again.',
  'auth.password_wrong': 'That is not the account’s password.',
  'error.storage_unavailable': 'The file storage is not responding. Try again in a moment.',
  'club.not_found': 'Club not found.',
  'club.legal_name_taken': 'A club with this legal name already exists.',
  'club.lnd_name_taken': 'Another club already carries this federation name.',
  'club.logo_invalid_type': 'The logo must be a PNG, JPEG or WebP image.',
  'club.logo_too_large': 'The logo file is too large.',
  'registration.document_invalid_type': 'The document must be a PDF or an image.',
  'registration.document_too_large': 'The document file is too large.',
  'registration.document_unknown': 'That upload is no longer there. Choose the file again.',
  'registration.already_pending': 'A request for this address is already being reviewed.',
  'registration.already_decided': 'That request has already been decided.',
  'registration.not_awaiting_document': 'That request is not waiting for a document.',
  'club.logo_missing': 'This club has no logo.',
  'club.cover_invalid_type': 'The cover must be a PNG, JPEG or WebP image.',
  'club.cover_too_large': 'The cover file is too large.',
  'club.cover_missing': 'This club has no cover.',
  'team_invite_link.already_active': 'This leva already has an active link: rotate it instead.',
  'team_invite_link.not_found':
    'This enrolment link is no longer valid. Ask whoever sent it for a new one.',
  'family_enrolment.already_pending': 'A request for this player is already being reviewed.',
  'family_enrolment.not_found': 'This request no longer exists.',
  'family_enrolment.already_decided': 'This request has already been reviewed.',
  'family_enrolment.guardian_confirmation_required':
    'Under 14, a request is approved only once a parent or guardian has confirmed it.',
  'family_enrolment.password_required': 'Choose a password for your access.',
  'family_enrolment.consent_required':
    'Tick the consent to the privacy notice to send the request.',
  'family_enrolment.verification_required':
    'Tick the declaration that you checked the data before confirming.',
  'family_enrolment.sign_in_required':
    'This e-mail already has a Volée account: sign in to continue.',
  'guardian_link.not_found': 'This link does not exist. Ask whoever sent it to send it again.',
  'guardian_link.expired':
    'This link has expired. Ask whoever sent it to make a new one from their page.',
  'guardian_link.closed': 'This request has already been answered.',
  'guardian_link.sign_in_required': 'This e-mail already has a Volée account: sign in to confirm.',
  'guardian_link.own_request':
    'You cannot confirm your own request: it takes a parent or guardian.',
  'guardian_link.consent_required': 'Tick the privacy notice to confirm.',
  'guardian_link.account_without_person':
    'Your account has no complete profile yet. Contact the club.',
  'guardian_link.guardian_under_age':
    'A guardian is an adult: this account cannot confirm. Sign in with a parent’s account.',
  'follower.not_found': 'This person no longer follows you.',
  'delegated_sign_in.unavailable':
    'A child cannot be added to this phone for now. Try again later.',
  'delegated_sign_in.no_account': 'Your child has no account of their own to add here yet.',
  'delegated_sign_in.rate_limited': 'You tried too many times in an hour. Try again later.',
  'guardian_link.child_access_required': 'Choose the username and the password for your child.',
  'family_enrolment.email_required':
    'From 14 you need an e-mail of your own: it is your access to Volée.',
  'family_enrolment.own_access_required':
    'They have turned 14 and have no e-mail of their own yet: approve once they have added one from their request page.',
  'account.username_invalid': 'Letters, digits and dots: from 4 to 20 characters.',
  'account.username_taken': 'This username is taken: choose another one.',
  'account.no_autonomy_notice': 'There is no notice to read for this account.',
  'account.own_email_required':
    'Add an e-mail of your own first: from 14 it is your access to Volée.',
  'child_access.not_found':
    'This child has no access with a username: their e-mail and password are their own.',
  'verification_code.invalid':
    'That code does not work: check you typed it right, or ask for a new one.',
  'verification_code.too_many': 'You asked for too many codes in an hour: try again later.',
  'account.email_in_use': 'This e-mail already has a Volée account: choose another one.',
  'account.has_a_mail': 'This account already has an e-mail.',
  'privacy_notice.not_found': 'The privacy notice is not available right now.',
  'parent_account.sign_in_required':
    'This e-mail already has a Volée account: sign in to continue.',
  'parent_account.consent_required': 'Tick the privacy notice to create your account.',
  'sign_up.persons_closed':
    'Players and parents join with an invitation or a link from their own club.',
  'kit.not_found': 'The club has no such kit.',
  'kit.second_needs_first': 'The club needs a first kit before a second one.',
  'kit.first_needed_by_second': 'The first kit cannot be removed while the club has a second.',
  'kit.secondary_color_required': 'This design needs a second colour.',
  'kit.secondary_color_not_allowed': 'A solid shirt has no second colour.',
  'onboarding.not_done':
    'Some steps are still to do: the first steps close once they are all done.',
  'onboarding.no_teams': 'The club has no teams to confirm yet.',
  'club.field_reserved_to_staff': 'Volee changes this field, not the club.',
  'club.pitch_not_linked': 'This pitch is not linked to the club.',
  'team.not_found': 'Team not found.',
  'team.already_exists': 'This club already fields that team.',
  'team.in_use': 'This team is enrolled in a league or has appointments, so it cannot be removed.',
  'team.fields_locked_by_enrollments':
    'This team is entered in a league group this season: age band, age limit and gender can no longer be changed. Remove the entry from the girone’s card first.',
  'team.cover_invalid_type': 'The cover must be a PNG, JPEG or WebP image.',
  'team.cover_too_large': 'The cover file is too large.',
  'team.cover_missing': 'This team has no cover.',
  'pitch.not_found': 'Pitch not found.',
  'pitch.lnd_name_taken': 'Another pitch already carries this federation name.',
  'pitch.lnd_code_taken': 'This ground code already belongs to another pitch.',
  'bulletin_source.not_found': 'This source of comunicati no longer exists.',
  'bulletin_source.name_taken': 'A source with this name already exists.',
  'bulletin_source.url_taken': 'A source with this address already exists.',
  'bulletin_source.inactive': 'The source is switched off: switch it on before checking it.',
  'bulletin_check.running': 'A check is already running on this source.',
  'club.alias_taken': 'This name already belongs to a club.',
  'pitch.alias_taken': 'This name already belongs to a pitch.',
  'gateway.not_found': 'Gateway not found.',
  'gateway.hardware_id_taken': 'A gateway with this hardware ID already exists.',
  'gateway.hostname_taken': 'A gateway with this hostname already exists.',
  'gateway.placement_incomplete': 'A pitch and an installation date go together.',
  'gateway.release_not_found': 'That gateway version has not been published to this environment.',
  'gateway.release_untried': 'No live kit runs {version} yet: upgrade one from its card first.',
  'gateway.secrets_unavailable':
    'The kit’s secrets cannot be read right now: try again in a few minutes.',
  'gateway.maintenance_pending': 'This action is already waiting for the kit: see the list below.',
  'gateway.retired': 'This kit is retired: it can no longer change.',
  'intervention.not_found': 'Intervention not found.',
  'intervention.open': 'This kit already has an open intervention: close or cancel it first.',
  'intervention.not_cancellable':
    'This intervention has already started, closed or been cancelled: it cannot be cancelled.',
  'bench.not_the_kits_board': 'A new SD goes into the kit’s own board: this box is another board.',
  'bench.no_repair_pending': 'This kit is no longer waiting for a board: reload the page.',
  'gateway.silent':
    'The kit is not talking: record the swap once it is back online, before unplugging the part.',
  'intervention.parts_mismatch': 'Give an outcome to every part of the kit, and only to those.',
  'bench.tailscale_unavailable':
    'Tailscale is not answering: try again in a few minutes; if it happens again, tell the tech team.',
  'bench.box_not_found':
    'The box is no longer heard: check that it is on and connected to the router, then try again.',
  'bench.box_busy':
    'This box already has a job: follow it on this page, or press Retry on the failed step.',
  'bench.router_code_mismatch':
    'This is not the code of the router connected to this box: scan the label of the router in front of you.',
  'bench.job_not_found': 'This job no longer exists: reload the page.',
  'bench.job_not_failed': 'This job is not stopped on an error: there is nothing to retry.',
  'bench.job_wrong_state': 'The job is not in the state this operation needs.',
  'bench.hello_too_large': 'The box sent a request that is too large.',
  'bench.full': 'Too many boxes at the bench right now.',
  'bench.not_configured': 'This environment is not set up for the bench yet.',
  'bench.no_open_job': 'This box has no open job: press Prepare on the Bench page.',
  'camera.not_found': 'Camera not found.',
  'recording.not_found': 'Recording not found.',
  'camera.mac_address_taken': 'A camera with this MAC address already exists.',
  'camera.serial_number_taken': 'A camera with this serial number already exists.',
  'camera.uid_taken': 'A camera with this UID already exists.',
  'player.not_found': 'Player not found.',
  'person.not_found': 'Person not found.',
  'person.already_exists': 'Someone with this name and date of birth is already on file.',
  'person.tax_code_taken': 'That codice fiscale is already on file for a different person.',
  'person.tax_code_conflicts': 'This person is already on file with a different codice fiscale.',
  'person.tax_code_required': 'The codice fiscale is required: nobody is registered without one.',
  'person.figc_number_taken': 'This matricola already belongs to another person.',
  'tabulato.file_missing': 'Choose at least one tabulato.',
  'tabulato.unreadable':
    'This file does not open as a tabulato: upload the .xlsx from the LND portal.',
  'tabulato.columns_unexpected':
    'This file is not an LND tabulato: its columns are not the expected ones. Nothing was loaded.',
  'tabulato.too_large': 'This file is too large to be a tabulato.',
  'tabulato.too_many_rows': 'We load at most {limit} rows at a time, and these tabulati have more.',
  'tabulato.too_many_files':
    'We load at most {limit} files at a time: put the categories together, or load the whole tabulato.',
  'tabulato.correction_unknown_row':
    'A correction is about a row the tabulato does not have: run the preview again.',
  'tabulato.changed': 'The file has changed: run the preview again.',
  'person.field_reserved_to_staff': 'Only Volee staff may change this field.',
  'person.minor_official_and_player':
    'A minor cannot be both a registered player and a club official.',
  'player.field_reserved_to_the_club': "The player's club writes this field.",
  'player.field_reserved_to_the_player': 'The player decides who reads their page.',
  'player.photo_invalid_type': 'The photo must be a PNG, JPEG or WebP image.',
  'player.photo_too_large': 'The photo file is too large.',
  'player.photo_missing': 'This player has no photo.',
  'staff.photo_invalid_type': 'The photo must be a PNG, JPEG or WebP image.',
  'staff.photo_too_large': 'The photo file is too large.',
  'staff.photo_missing': 'There is no photo to remove.',
  'profile.not_public': 'This player does not show their profile.',
  'registration.not_found': 'Registration not found.',
  'registration.player_already_registered':
    'Already registered for this season. A release from the previous club is needed first.',
  'registration.after_season_end': 'The registration date falls after this season ended.',
  'registration.release_before_registration':
    'The release date cannot come before the registration date.',
  'registration.release_in_the_future': 'The release date cannot be in the future.',
  'registration.season_not_current':
    'This registration belongs to another season: Volée changes it.',
  'registration.field_reserved_to_staff': 'Volée changes the registration date.',
  'season.not_found': 'That season does not exist.',
  'season.bounds_conflict': 'Rows in this season are dated after the boundary you set.',
  'season.overlaps': 'Those dates overlap another season.',
  'season.dates_out_of_order': 'A season cannot end before it starts.',
  'league_category.not_found': 'Category not found.',
  'league_category.already_exists':
    'A category with this age band, age limit, tier and gender already exists.',
  'league_category.in_use': 'This category already has league groups, so it cannot be deleted.',
  'league_category.fields_locked_by_groups':
    'This category already runs {group_count} league groups: age band, age limit, tier and gender can no longer be changed.',
  'league_group.not_found': 'League group not found.',
  'league_group.already_exists':
    'A league group with this code already exists for this category and season.',
  'league_group.in_use': 'This league group has enrolled teams, so it cannot be deleted.',
  'enrollment.not_found': 'Enrollment not found.',
  'enrollment.already_exists': 'This team is already enrolled in this league group.',
  'enrollment.team_category_mismatch':
    'This team does not match the league group: it needs {age_group}, {under_age}, {gender}.',
  'enrollment.in_use': 'This enrollment has matches or call-ups, so it cannot be removed.',

  'match.not_found': 'Fixture not found.',
  'match.already_exists': 'These two teams already meet in this leg.',
  'match.in_use': 'This fixture has a recording attached, so it cannot be deleted.',
  'match.sides_equal': 'A team cannot play itself: pick a different away side.',
  'match.side_not_in_group': 'This team is not entered in this league group.',
  'match.side_withdrawn': 'This team has withdrawn and cannot be given a new fixture.',
  'match.side_already_plays_that_matchday': 'This team already plays on matchday {matchday}.',
  'match.already_played': 'This fixture has been played: it cannot be deleted or rescheduled.',
  'match.kickoff_before_season':
    'The kick-off is before the {season_year} season opens, on {starts_on}.',
  'match_change_report.not_found': 'This report does not exist.',
  'match_change_report.match_played':
    'The match has been played: a change can no longer be reported.',
  'match_change_report.says_nothing':
    'Say at least one thing that changes: the date, the ground, or that it is postponed.',
  'match_change_report.postponed_with_date':
    'A match postponed to a date to be decided has no new date.',
  'match_change_report.already_closed': 'This report has already been closed.',
  'team_sheet.side_not_in_match': 'This side does not belong to this match.',
  'team_sheet.shirt_duplicated': 'Two called-up players share the same shirt number.',
  'team_sheet.too_many_starters': 'No more than 11 starters are allowed.',
  'team_sheet.too_many_call_ups': 'No more than 20 players can be called up.',
  'team_sheet.captaincy_duplicated': 'The captain or vice-captain role is assigned twice.',
  'team_sheet.no_call_ups': 'Nobody is called up: publishing needs at least one player.',
  'team_sheet.call_ups_locked':
    'The match has started: a published call-up no longer changes who is called up, only the numbers.',
  'team_sheet.details_locked':
    'The match has started: the meeting and the notes of the call-up can no longer be changed.',
  'team_sheet.locked_by_analysis':
    'The call-up has already gone to the analysis: the numbers can no longer be changed.',
  'team_sheet.call_up_not_found': 'This player is not called up for this match.',
  'team_sheet.not_published':
    'The call-up is not published yet: there is nothing to answer for now.',
  'team_sheet.answers_closed': 'The match has started: the answer can no longer be changed.',
  'team_sheet.registration_not_in_club': 'This person is not registered with this club.',
  'team_sheet.registration_repeated': 'The same registrant is called up twice.',
  'team_sheet.club_kit_missing': 'The club does not have the kit chosen for this match.',
  'team_sheet.team_kit_missing': 'The team does not have the kit chosen for this match.',
  'team_sheet.meeting_place_without_time':
    'A meeting place needs a meeting time: add the time, or remove the place.',
  'appointment.already_exists': 'This appointment is already assigned for the season.',
  'appointment.not_found': 'Appointment not found.',
  'appointment.already_ended': 'This appointment has already ended.',
  'appointment.role_forbids_team': 'This role is club-wide, so it takes no team.',
  'appointment.field_reserved_to_staff': 'Only Volée staff can change this.',
  'appointment.last_official': 'The club would be left with no club officials.',
  'appointment.end_after_season': 'The end date falls after the season has ended.',
  'appointment.seats_full': 'The two team seats are taken: free one up.',
  'appointment.officials_full': 'The two club seats are taken: free one up.',
  'appointment.seat_without_team': 'To give a team seat, choose the team.',
  'appointment.not_on_a_team': 'This appointment is not on a team.',
  'appointment.seat_already_held': 'This person already holds a seat on this team.',
  'appointment.official_takes_no_team_seat': 'A club official does not take a team seat.',
  'team.not_in_club': 'That team belongs to another club.',
  'team.player_already_in_roster': 'This player is already in the roster.',
  'team.player_not_in_roster': 'This player is not in the roster.',
  'registration.not_in_club': 'This player is not registered with your club.',
  'registration.released': 'This player has been released and cannot join a roster.',
  'registration.not_in_season': 'This registration belongs to another season.',
  'registration.repeated': 'The same player is in the selection twice.',
  'registration.already_in_a_team':
    'This registrant is already in another squad’s roster: take them out of it first.',
  'invitation.not_found': 'Invitation not found.',
  'invitation.already_settled': 'This invitation has already been accepted or revoked.',
  'invitation.already_pending':
    'There is already a pending invitation for this address and this role: resend it from the list.',
  'invitation.person_id_reserved': 'Only Volée can pick a person from the registry.',
  'invitation.email_not_the_account':
    'This person already has an account: the invitation goes to their address.',
  'invitation.team_required': 'You invite onto a seat only: choose the team.',
  'invitation.sign_in_required':
    'This address already has a Volée account: sign in to accept the incarico.',
  'invitation.other_account':
    'The invitation is for another account: sign out and sign in with the address it was sent to.',
  'invitation.no_account': 'This address has no account yet: fill in your details to create one.',
  'activation.person_has_account':
    'An account with this name and date of birth already exists. If it is yours, sign in with it and ask whoever invited you to use its address.',
  'activation.person_at_another_address':
    'The registry already has a person with this name and date of birth. Ask whoever invited you to do it from your record, or ask Volée.',
  // The activation endpoint tells the four apart; here they are only ever read in a log or
  // a toast, so they stay plain statements of fact.
  'invitation.token_unknown': 'No invitation matches that link.',
  'invitation.expired': 'That invitation has expired.',
  'invitation.already_accepted': 'That invitation has already been accepted.',
  'invitation.revoked': 'That invitation has been revoked.',
  'account.not_found': 'This account does not exist.',
  'account.no_identity': 'This account has no sign-in identity yet.',
  'account.email_taken': 'A Volée account with this email already exists.',
  'account.bound_to_another_person':
    'That address is already the account of another person: invite them at the address they use in Volée, or move the account first.',
  'account.person_already_bound':
    'This person already has a Volée account at a different address: use that one, or move the account first.',
  'account.identity_already_exists': 'That email already exists in the identity provider.',
  'account.password_rejected': 'The chosen password does not meet the pool policy.',
  // Known to the identity provider but not to us: nothing the admin can fix from a form.
  'staff.identity_already_exists':
    'This email already exists in the identity provider but not in Volée: it needs a technical fix.',
  'staff.invite_quota_exceeded':
    'The invitation email could not be sent — the daily limit may have been reached. Try again later.',
  'subscription.not_a_player': 'Only a registered player can be subscribed.',
  'subscription.not_a_guardian':
    'Only the player themselves or one of their parents can subscribe them.',
  'subscription.guardian_pays': 'Under 14, a parent or guardian of yours starts the plan.',
  'subscription.already_active': 'This player is already subscribed.',
  'subscription.not_the_payer': 'Only the person paying for the subscription can change it.',
  'subscription.not_active': 'This plan is no longer active.',
  'billing.provider_unavailable': 'The payment service is not responding. Try again shortly.',
  'match_analysis.no_video': 'This match cannot be sent yet: no video of it has been uploaded.',
  'match_analysis.no_published_team_sheet':
    'This match cannot be sent yet: neither team has published its call-up.',
  'match_analysis.provider_unavailable':
    'The match analysis service is not responding. Try again shortly.',
  'match_analysis.switch_unavailable':
    'Sending for analysis is off for this environment and cannot be turned on from here.',
  'match_analysis.provider_refused':
    'The match analysis service refused this match. The details are in the logs.',
  'match_analysis.unknown_match_format':
    'This match cannot be sent: the category of the home team has no known match format (players and periods).',
  'category.not_admitted':
    'This category is not one Volée admits: pick it from the list. The female categories are Under 15, Under 17, Under 19 and Seniores.',
  'highlights.not_your_library':
    'Only the player and their guardians change what is featured, the favourites and the playlists.',
  'highlights.clip_not_found': 'This clip is no longer there.',
  'highlights.playlist_not_found': 'This playlist is no longer there.',
  'highlights.plan_required': 'The showcase, favourites and playlists need the plan.',
  'webhook.bad_signature': 'Invalid webhook signature.',
}

/** Shown when the backend sends a `code` this catalogue does not know. */
export const EN_UNKNOWN_ERROR = 'Something went wrong. Try again in a moment.'

/** Shown when the network never answered: there is no `code` to translate. */
export const EN_NETWORK_ERROR = 'No connection available. Check the network and try again.'
